import { useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";

export interface Filters {
  sizes: string[];
  categories: string[];
  maxPrice: number;
  maxDistance: number;
}

interface FilterBarProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
}

const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
const CATEGORIES = [
  { value: "wedding", label: "Wedding" },
  { value: "engagement", label: "Engagement" },
  { value: "party", label: "Party" },
  { value: "formal", label: "Formal" },
  { value: "casual", label: "Casual" },
];

export function FilterBar({ filters, onChange }: FilterBarProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Filters>(filters);

  const activeCount =
    filters.sizes.length +
    filters.categories.length +
    (filters.maxPrice < 2000 ? 1 : 0) +
    (filters.maxDistance < 50 ? 1 : 0);

  const toggleSize = (size: string) => {
    setDraft(d => ({
      ...d,
      sizes: d.sizes.includes(size) ? d.sizes.filter(s => s !== size) : [...d.sizes, size],
    }));
  };

  const toggleCategory = (cat: string) => {
    setDraft(d => ({
      ...d,
      categories: d.categories.includes(cat) ? d.categories.filter(c => c !== cat) : [...d.categories, cat],
    }));
  };

  const apply = () => {
    onChange(draft);
    setOpen(false);
  };

  const reset = () => {
    const fresh: Filters = { sizes: [], categories: [], maxPrice: 2000, maxDistance: 50 };
    setDraft(fresh);
    onChange(fresh);
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
      {/* Filter button */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full flex-shrink-0 gap-1.5 border-border/60 hover:border-border"
          >
            <SlidersHorizontal size={13} />
            <span className="text-xs">Filters</span>
            {activeCount > 0 && (
              <span className="w-4 h-4 rounded-full text-white text-xs flex items-center justify-center" style={{ background: "var(--rose)" }}>
                {activeCount}
              </span>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="text-base font-semibold">Filter Dresses</SheetTitle>
          </SheetHeader>

          {/* Size */}
          <div className="mb-6">
            <p className="text-sm font-medium mb-3">Size</p>
            <div className="flex flex-wrap gap-2">
              {SIZES.map(s => (
                <button
                  key={s}
                  onClick={() => toggleSize(s)}
                  className={`px-4 py-2 rounded-full text-sm border transition-all ${
                    draft.sizes.includes(s)
                      ? "border-rose-400 text-rose-600 bg-rose-50"
                      : "border-border text-muted-foreground hover:border-muted-foreground"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Category */}
          <div className="mb-6">
            <p className="text-sm font-medium mb-3">Event / Category</p>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(c => (
                <button
                  key={c.value}
                  onClick={() => toggleCategory(c.value)}
                  className={`px-4 py-2 rounded-full text-sm border transition-all ${
                    draft.categories.includes(c.value)
                      ? "border-rose-400 text-rose-600 bg-rose-50"
                      : "border-border text-muted-foreground hover:border-muted-foreground"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Price */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium">Max Price</p>
              <span className="text-sm font-semibold" style={{ color: "var(--rose)" }}>{draft.maxPrice} SAR</span>
            </div>
            <Slider
              min={50}
              max={2000}
              step={50}
              value={[draft.maxPrice]}
              onValueChange={([v]) => setDraft(d => ({ ...d, maxPrice: v }))}
              className="w-full"
            />
          </div>

          {/* Distance */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium">Max Distance</p>
              <span className="text-sm font-semibold" style={{ color: "var(--rose)" }}>{draft.maxDistance} km</span>
            </div>
            <Slider
              min={1}
              max={50}
              step={1}
              value={[draft.maxDistance]}
              onValueChange={([v]) => setDraft(d => ({ ...d, maxDistance: v }))}
              className="w-full"
            />
          </div>

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1 rounded-full" onClick={reset}>
              <X size={13} className="mr-1" />
              Reset
            </Button>
            <Button className="flex-1 rounded-full" style={{ background: "var(--rose)" }} onClick={apply}>
              Apply Filters
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Quick category chips */}
      {CATEGORIES.map(c => (
        <button
          key={c.value}
          onClick={() => {
            const newCats = filters.categories.includes(c.value)
              ? filters.categories.filter(x => x !== c.value)
              : [...filters.categories, c.value];
            onChange({ ...filters, categories: newCats });
          }}
          className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs border transition-all ${
            filters.categories.includes(c.value)
              ? "border-rose-400 text-rose-600 bg-rose-50"
              : "border-border/60 text-muted-foreground hover:border-muted-foreground"
          }`}
        >
          {c.label}
        </button>
      ))}

      {/* Active filters */}
      {filters.sizes.map(s => (
        <Badge
          key={s}
          variant="secondary"
          className="flex-shrink-0 rounded-full cursor-pointer hover:bg-destructive/10 transition-colors"
          onClick={() => onChange({ ...filters, sizes: filters.sizes.filter(x => x !== s) })}
        >
          Size {s} <X size={10} className="ml-1" />
        </Badge>
      ))}
    </div>
  );
}
